import React, { useState, useEffect, memo } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, Plus } from "lucide-react";
import { Expert, DEFAULT_EXPERT } from "@/models/Expert";
import { expertService } from "@/services/expertService";
import { EXPERTS_DATA } from "@/data/experts-data";
import { cn } from "@/lib/utils";

interface ExpertsListProps {
  selectedExpertId: string;
  onSelectExpert: (expertId: string) => void;
  onClose?: () => void;
  isMobile?: boolean;
}

/**
 * Component hiển thị danh sách experts theo phong cách DeepSeek
 */
const ExpertsListBase: React.FC<ExpertsListProps> = ({
  selectedExpertId,
  onSelectExpert,
  onClose,
  isMobile = false
}) => {
  const { t } = useTranslation();
  const [experts, setExperts] = useState<Expert[]>(EXPERTS_DATA);
  const [searchQuery, setSearchQuery] = useState<string>("");
  
  // Fetch experts
  useEffect(() => {
    const fetchExperts = async () => {
      try {
        const expertsList = await expertService.getAllExperts();
        
        // Loại bỏ các chuyên gia trùng lặp dựa trên ID sử dụng Map
        const uniqueExpertsMap = new Map<string, Expert>();
        
        // Thêm chuyên gia mặc định (Thiền Sư AI) trước tiên
        uniqueExpertsMap.set("default", DEFAULT_EXPERT);
        
        // Thêm các chuyên gia khác, bỏ qua nếu đã có ID "default" (Thiền Sư AI)
        for (const expert of expertsList) {
          if (expert.id !== "default" && !uniqueExpertsMap.has(expert.id)) {
            uniqueExpertsMap.set(expert.id, expert);
          }
        }
        
        // Chuyển Map thành mảng
        const uniqueExperts = Array.from(uniqueExpertsMap.values());
        
        console.log(`Fetched ${expertsList.length} experts, filtered to ${uniqueExperts.length} unique experts`);
        setExperts(uniqueExperts);
      } catch (error) {
        console.error("Error fetching experts:", error);
        // Fallback to sample data
        setExperts(EXPERTS_DATA);
      }
    };
    
    fetchExperts();
  }, []);
  
  // Lọc experts theo tìm kiếm
  const filteredExperts = searchQuery.trim() === "" 
    ? experts 
    : experts.filter(expert => 
        expert.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (expert.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (expert.description || "").toLowerCase().includes(searchQuery.toLowerCase())
      );
      
  return (
    <div className="flex flex-col h-full">
      {/* Tìm kiếm expert */}
      <div className="p-3">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("search_experts", { ns: "ai" })}
            className="pl-8 bg-background"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      
      {/* Danh sách experts */}
      <div className="space-y-1 px-2">
        <div className="text-xs font-medium text-muted-foreground uppercase px-2 py-1.5">
          {t("my_experts", { ns: "ai" })}
        </div>
      </div>
      
      {/* Danh sách experts - thêm max-h-[calc(100vh-150px)] để giới hạn chiều cao và buộc phải scroll */}
      <div className="overflow-y-auto max-h-[calc(100vh-150px)] flex-1 px-2">
        {filteredExperts.map((expert) => (
          <div
            key={expert.id}
            className={cn(
              "expert-item flex items-center gap-3 px-2 py-2 cursor-pointer rounded-md transition-colors my-1",
              selectedExpertId === expert.id 
                ? "bg-primary/5 text-primary" 
                : "hover:bg-muted/60"
            )}
            onClick={() => {
              console.log('Expert item clicked:', expert.name);
              onSelectExpert(expert.id);
            }}
          >
            <Avatar className="expert-avatar h-7 w-7 ring-1 ring-primary/10">
              <AvatarImage src={expert.avatar} alt={expert.name} />
              <AvatarFallback>{expert.name.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="expert-info overflow-hidden">
              <div className="expert-name font-medium text-sm truncate">{expert.name}</div>
            </div>
          </div>
        ))}
        
        {filteredExperts.length === 0 && (
          <div className="p-4 text-center text-muted-foreground">
            {t("no_experts_found", { ns: "ai" })}
          </div>
        )}
      </div>
    </div>
  );
};

// Memo hóa component để ngăn re-render không cần thiết
const ExpertsList = memo(ExpertsListBase, (prevProps, nextProps) => {
  // So sánh props để quyết định có re-render hay không
  return (
    prevProps.selectedExpertId === nextProps.selectedExpertId &&
    prevProps.isMobile === nextProps.isMobile
  );
});

export default ExpertsList; 